import React, { useEffect, useState } from 'react';
import { IUser } from '../../../domain/user.interface';
import { EGroupStateKeys, IGroupResume } from '../../../domain/group.interface';
import {
  EUserType,
  ITypeaheadItem,
  ITypeaheadList,
  minQueryLength,
  optionContactConfig,
  optionUserConfig,
} from '../../../domain/typeahead.interfaces';
import { ShareWishModalFormComponent } from '../organisms.index';
import { getUsers, resetUsers } from '../../../store/users/users.actions';
import { useDispatch, useSelector } from 'react-redux';
import { typeaheadItemFormatter } from '../../../utils/formatter';
import {
  loadWishListCatalogue,
  resetWishList,
  resetWishListCatalogue,
  shareWish,
} from '../../../store/wishlist/wishlist.actions';
import { makeSelectWishListCatalogue } from '../../../store/wishlist/wishlist.selectors';
import { makeSelectGroupList } from '../../../store/grouplist/grouplist.selectors';

import { makeSelectCurrentUser } from '../../../store/current-user/current-user.selectors';
import { makeSelectUsers } from '../../../store/users/users.selectors';
import { debounce } from 'lodash';
import {
  loadGroupList,
  resetGroup,
} from '../../../store/grouplist/grouplist.actions';

interface Props {
  wishListId: string;
  wishId: string;
  showModal: boolean;
  handleClose: () => void;
}

export default function ShareWishModalComponent(props: Props) {
  const dispatch = useDispatch();
  const { wishListId, wishId, handleClose } = props;
  const wishListCatalogueReducer = useSelector(makeSelectWishListCatalogue());
  const groupListReducer = useSelector(makeSelectGroupList());
  const users = useSelector(makeSelectUsers());
  const currentUser = useSelector(makeSelectCurrentUser());
  const isAuthenticated = currentUser && !!currentUser.id;
  const [userQuery, setUserQuery] = useState<string | undefined>('');
  const [selectedUserShare, setSelectedUserShare] = useState<IUser[]>([]);
  const [selectedGroupShare, setSelectedGroupShare] = useState<IGroupResume[]>(
    [],
  );
  const [selectedListShare, setSelectedListShare] = useState<any[]>([]);
  const [shareUserRef, setShareUserRef] = useState<any>(undefined);
  const [shareUserOptions, setShareUserOptions] = useState<
    ITypeaheadList[] | undefined
  >();
  const [groupListOptions, setGroupListOptions] = useState(groupListReducer);
  const [wishListOptions, setWishListOptions] = useState(
    wishListCatalogueReducer,
  );
  const handleInputChange = (pattern: string) => {
    setUserQuery(pattern.trim().toLowerCase());
  };

  const fetchUsers = () => {
    dispatch(getUsers(userQuery as string));
    return true;
  };

  const debouncedFetchUsers = debounce(fetchUsers, 300, { maxWait: 1000 });

  useEffect(() => {
    if (!userQuery || userQuery.trim().length < minQueryLength) {
      dispatch(resetUsers());
      return;
    }
    debouncedFetchUsers();
    // eslint-disable-next-line
  }, [userQuery]);

  useEffect(() => {
    return () => {
      dispatch(resetWishList());
      if (isAuthenticated) {
        dispatch(resetGroup(EGroupStateKeys.groupList));
        dispatch(resetWishListCatalogue());
      }
    };
  }, []);

  useEffect(() => {
    if (wishId && isAuthenticated) {
      if (!groupListOptions.length) {
        dispatch(loadGroupList());
      }
      if (!wishListOptions.length) {
        dispatch(loadWishListCatalogue());
      }
    }
    // eslint-disable-next-line
  }, [wishId]);

  useEffect(() => {
    const filteredUsers = users.filter(
      (el: any) =>
        !selectedUserShare.some((user: ITypeaheadItem) => el.id === user.id) &&
        el.id !== currentUser.id,
    );
    const apiContacts = filteredUsers
      .filter((el: any) => el.isContact)
      .map((user: any) => {
        const userType = user.isActive ? EUserType.CONTACT : EUserType.INACTIVE;
        return typeaheadItemFormatter(optionContactConfig, user, userType);
      });
    const apiUsers = filteredUsers
      .filter((el: any) => !el.isContact)
      .map((user: any) => {
        return typeaheadItemFormatter(optionUserConfig, user, EUserType.USER);
      });
    const shareUserOptionsConfig: ITypeaheadList[] = [
      {
        title: 'contacts',
        data: apiContacts,
        onClickAction: handleAddNewSelectedUserShare,
      },
      {
        title: 'users',
        data: apiUsers,
        onClickAction: handleAddNewSelectedUserShare,
      },
    ];
    setShareUserOptions(shareUserOptionsConfig);
    // eslint-disable-next-line
  }, [users, selectedUserShare]);

  useEffect(() => {
    const filteredGroupListOptions = groupListReducer.filter(
      (el: any) =>
        !selectedGroupShare.some((group: IGroupResume) => group.id === el.id),
    );
    setGroupListOptions(filteredGroupListOptions);
  }, [groupListReducer, selectedGroupShare]);

  useEffect(() => {
    const filteredWishListOptions = wishListCatalogueReducer.filter(
      (el: any) =>
        el.id !== +wishListId &&
        !selectedListShare.some((list: any) => list.id === el.id),
    );
    setWishListOptions(filteredWishListOptions);
    // eslint-disable-next-line
  }, [wishListCatalogueReducer, selectedListShare]);

  const handleShareModalClose = () => {
    if (!!shareUserRef) {
      shareUserRef.getInstance().clear();
    }
    dispatch(resetUsers());
    setSelectedUserShare([]);
    setSelectedGroupShare([]);
    setSelectedListShare([]);
    handleClose();
  };

  const handleOnBlurUserShare = () => {
    dispatch(resetUsers());
    shareUserRef.getInstance().clear();
    setUserQuery('');
  };

  function submitShareForm() {
    if (wishId) {
      const userIdsArr = selectedUserShare.map(
        (user: ITypeaheadItem) => user.id,
      );
      const groupIdArr = selectedGroupShare.map(
        (group: IGroupResume) => group.id,
      );
      const listIdArr = selectedListShare.map((list: any) => list.id);
      const body = {
        userId: userIdsArr,
        groupId: groupIdArr,
        wishLists: listIdArr,
      };
      shareWish(body, wishId).then();
      handleShareModalClose();
    } else {
      handleShareModalClose();
    }
  }

  const handleAddNewSelectedUserShare = (shareSelectedUser: IUser) => {
    if (!!shareUserRef) {
      shareUserRef.getInstance().blur();
    }
    setSelectedUserShare([...selectedUserShare, shareSelectedUser]);
  };

  const handleAddNewSelectedGroupShare = (shareSelectedGroupId: string) => {
    setSelectedGroupShare([
      ...selectedGroupShare,
      ...groupListReducer.filter(
        (group: IGroupResume) => group.id === shareSelectedGroupId,
      ),
    ]);
  };

  const handleAddNewSelectedListShare = (shareSelectedListId: string) => {
    setSelectedListShare([
      ...selectedListShare,
      ...wishListCatalogueReducer.filter(
        (list: any) => list.id === shareSelectedListId,
      ),
    ]);
  };

  const handleRemoveSelectedUserShare = (userId: string) => {
    setSelectedUserShare(
      selectedUserShare.filter((user: ITypeaheadItem) => user.id !== userId),
    );
  };

  const handleRemoveSelectedGroupShare = (groupId: string) => {
    setSelectedGroupShare(
      selectedGroupShare.filter((group: IGroupResume) => group.id !== groupId),
    );
  };

  const handleRemoveSelectedListShare = (id: string) => {
    setSelectedListShare(
      selectedListShare.filter((list: any) => list.id !== id),
    );
  };

  return (
    <ShareWishModalFormComponent
      {...props}
      handleClose={handleShareModalClose}
      options={shareUserOptions}
      groupListOptions={groupListOptions}
      wishListOptions={wishListOptions}
      addRef={(r: any) => setShareUserRef(r)}
      handleInputChange={handleInputChange}
      handleOnBlur={handleOnBlurUserShare}
      handleAddNewSelectedGroup={handleAddNewSelectedGroupShare}
      handleAddNewSelectedList={handleAddNewSelectedListShare}
      handleRemoveSelectedUser={handleRemoveSelectedUserShare}
      handleRemoveSelectedGroup={handleRemoveSelectedGroupShare}
      handleRemoveSelectedList={handleRemoveSelectedListShare}
      submitSelected={submitShareForm}
      selectedUserShare={selectedUserShare}
      selectedGroupShare={selectedGroupShare}
      selectedListShare={selectedListShare}
    />
  );
}
